import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Button } from "@/components/ui/Button";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import {
  diffWorkInstruction,
  renderWorkInstructionHtml,
  type WorkInstructionBody,
} from "@integrator-src/documents/conformal-coat-callout";

type InstructionChange = {
  fieldName: string | null;
  changedFrom: string | null;
  changedTo: string | null;
  reason: string | null;
  createdAt: string;
};

type InstructionSummary = {
  documentId: string;
  title: string;
  revision: string;
  lifecycleStatus: string;
  date: string;
};

type InstructionDocument = {
  documentId: string;
  revision: string;
  lifecycleStatus: string;
  title: string;
  body: WorkInstructionBody;
  changes: InstructionChange[];
};

const LIST_KEY = ["documents", "work-instructions"] as const;

function statusLabel(status: string): string {
  if (status === "current") return "Released";
  if (status === "wip") return "Work in progress";
  if (status === "draft") return "Draft revision";
  return status;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "The work instruction could not be saved.";
}

export function WorkInstructionsPage(): JSX.Element {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const writesAllowed = useHubModuleWrite("documents");
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const listQuery = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => apiFetch<{ documents: InstructionSummary[] }>("/api/documents/work-instructions"),
  });
  const createMutation = useMutation({
    mutationFn: () => apiFetch<{ document: InstructionDocument }>("/api/documents/work-instructions", { method: "POST" }),
    onSuccess: async (result) => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: LIST_KEY });
      navigate(`/documents/work-instructions/${result.document.documentId}`);
    },
    onError: (cause) => setError(errorMessage(cause)),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="Create and edit work instructions. Revision A is the first version. Saving a later revision records each changed field. Release the work-in-progress revision when it should replace the previous one.">
          Work instructions
        </PageTitleWithHelp>
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={!writesAllowed || createMutation.isPending} onClick={() => createMutation.mutate()}>
            New work instruction
          </Button>
          <Link className="inline-flex items-center text-sm text-sky-800 hover:underline" to="/documents/workbench">
            Document workbench
          </Link>
        </div>
      </div>
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
      {listQuery.isError ? <p className="text-sm text-red-700">{errorMessage(listQuery.error)}</p> : null}
      <div className="grid items-start gap-4 xl:grid-cols-[16rem_minmax(0,1fr)]">
        <ul className="space-y-1 rounded-lg border border-slate-200 bg-white p-2">
          {(listQuery.data?.documents ?? []).map((document) => (
            <li key={document.documentId}>
              <Link
                className={`block rounded-md px-2 py-1.5 text-sm hover:bg-slate-50 ${document.documentId === documentId ? "bg-slate-100 font-medium" : ""}`}
                to={`/documents/work-instructions/${document.documentId}`}
              >
                <span className="block text-slate-900">{document.title}</span>
                <span className="text-xs text-slate-500">
                  Revision {document.revision} · {statusLabel(document.lifecycleStatus)}
                </span>
              </Link>
            </li>
          ))}
          {listQuery.data && listQuery.data.documents.length === 0 ? (
            <li className="px-2 py-1.5 text-sm text-slate-600">No work instructions yet.</li>
          ) : null}
        </ul>
        {documentId ? <WorkInstructionEditor documentId={documentId} /> : <p className="text-sm text-slate-600">Choose a work instruction, or create one.</p>}
      </div>
    </div>
  );
}

function WorkInstructionEditor(props: { documentId: string }): JSX.Element {
  const writesAllowed = useHubModuleWrite("documents");
  const queryClient = useQueryClient();
  const editorRef = useRef<HTMLDivElement>(null);
  const [body, setBody] = useState<WorkInstructionBody | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: [...LIST_KEY, props.documentId],
    queryFn: () => apiFetch<{ document: InstructionDocument }>(`/api/documents/work-instructions/${props.documentId}`),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const document = query.data?.document ?? null;
  const loadedKey = document ? `${document.documentId}:${document.revision}:${document.changes.length}` : "";
  useEffect(() => {
    const current = query.data?.document;
    if (!current || current.documentId !== props.documentId) return;
    setBody(current.body);
    setReason("");
    setError(null);
    if (editorRef.current) editorRef.current.innerHTML = current.body.contentHtml;
  }, [loadedKey, props.documentId, query.data]);

  const editable = writesAllowed && (document?.lifecycleStatus === "wip" || document?.lifecycleStatus === "draft");
  const changes = useMemo(() => (document && body ? diffWorkInstruction(document.body, body) : []), [document, body]);
  const dirty = changes.length > 0;
  const preview = body && document ? renderWorkInstructionHtml(body, document.revision) : "";

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!document || !body) throw new Error("Nothing to save.");
      const contentHtml = editorRef.current?.innerHTML ?? body.contentHtml;
      return apiFetch<{ document: InstructionDocument }>(`/api/documents/work-instructions/${document.documentId}`, {
        method: "PUT",
        body: { body: { ...body, contentHtml }, reason },
      });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: LIST_KEY });
      await queryClient.invalidateQueries({ queryKey: [...LIST_KEY, props.documentId] });
    },
    onError: (cause) => setError(errorMessage(cause)),
  });
  const nextRevisionMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ document: InstructionDocument }>(`/api/documents/work-instructions/${props.documentId}/next-revision`, {
        method: "POST",
      }),
    onSuccess: async (result) => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: LIST_KEY });
      window.location.assign(`/documents/work-instructions/${result.document.documentId}`);
    },
    onError: (cause) => setError(errorMessage(cause)),
  });
  const releaseMutation = useMutation({
    mutationFn: () => apiFetch(`/api/documents/${props.documentId}/release-wip`, { method: "POST" }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: LIST_KEY });
      await queryClient.invalidateQueries({ queryKey: [...LIST_KEY, props.documentId] });
    },
    onError: (cause) => setError(errorMessage(cause)),
  });

  if (query.isLoading) return <p className="text-sm text-slate-600">Loading the work instruction.</p>;
  if (query.isError) return <p className="text-sm text-red-700">{errorMessage(query.error)}</p>;
  if (!document || !body) return <p className="text-sm text-slate-600">This work instruction is not available.</p>;

  return (
    <div className="space-y-3">
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-800">Revision {document.revision}</span>
        <span>{statusLabel(document.lifecycleStatus)}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {editable ? (
          <HubSaveButton
            dirty={dirty}
            disabled={!writesAllowed || saveMutation.isPending}
            isLoading={saveMutation.isPending}
            dirtyHint={changes.map((change) => change.fieldName).join(", ")}
            onClick={() => saveMutation.mutate()}
          >
            Save changes
          </HubSaveButton>
        ) : null}
        {document.lifecycleStatus === "current" ? (
          <Button type="button" variant="secondary" disabled={!writesAllowed || nextRevisionMutation.isPending} onClick={() => nextRevisionMutation.mutate()}>
            Start next revision
          </Button>
        ) : null}
        {document.lifecycleStatus === "wip" && !dirty ? (
          <Button type="button" variant="secondary" disabled={!writesAllowed || releaseMutation.isPending} onClick={() => releaseMutation.mutate()}>
            Release
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            const blob = new Blob([preview], { type: "text/html" });
            const url = URL.createObjectURL(blob);
            const opened = window.open(url);
            if (!opened) return;
            opened.addEventListener("load", () => opened.print());
          }}
        >
          Print
        </Button>
      </div>
      {document.lifecycleStatus === "current" ? (
        <p className="text-sm text-slate-600">This revision is released. Start the next revision to edit it.</p>
      ) : null}
      <div className="grid items-start gap-4 xl:grid-cols-2">
        <div className="space-y-3">
          {editable ? (
            <label className="block text-sm font-medium text-slate-700">
              Change note
              <input
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm font-normal"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
          ) : null}
          <fieldset disabled={!editable} className="grid gap-3 border-0 p-0 sm:grid-cols-2">
            <Field label="Title" value={body.title} onChange={(title) => setBody({ ...body, title })} />
            <Field label="Subtitle" value={body.subtitle} onChange={(subtitle) => setBody({ ...body, subtitle })} />
            <Field label="Date" value={body.date} onChange={(date) => setBody({ ...body, date })} />
            <Field label="Company" value={body.company} onChange={(company) => setBody({ ...body, company })} />
            <Field label="Drawing" value={body.drawing} onChange={(drawing) => setBody({ ...body, drawing })} />
            <Field label="Assemblies" value={body.assemblies} onChange={(assemblies) => setBody({ ...body, assemblies })} />
          </fieldset>
          <div
            ref={editorRef}
            className="min-h-[24rem] rounded-lg border border-slate-300 bg-white p-4 text-sm"
            contentEditable={editable}
            suppressContentEditableWarning
            onInput={() => {
              if (!editorRef.current) return;
              setBody((current) => (current ? { ...current, contentHtml: editorRef.current?.innerHTML ?? current.contentHtml } : current));
            }}
          />
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-slate-900">Changes on this revision</h2>
            {document.changes.length === 0 ? (
              <p className="text-sm text-slate-600">No field changes are recorded on this revision yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {document.changes.map((change, index) => (
                  <li key={`${change.createdAt}-${change.fieldName}-${index}`} className="rounded-md border border-slate-200 px-3 py-2">
                    <div className="font-medium">{change.fieldName ?? "Note"}</div>
                    {change.fieldName !== "Body" && (change.changedFrom || change.changedTo) ? (
                      <div className="mt-1 text-slate-600">
                        {change.changedFrom || "(empty)"} → {change.changedTo || "(empty)"}
                      </div>
                    ) : null}
                    {change.reason ? <div className="mt-1 text-slate-700">{change.reason}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        <iframe title="Work instruction preview" className="h-[80vh] w-full rounded-lg border border-slate-300 bg-slate-200" srcDoc={preview} />
      </div>
    </div>
  );
}

function Field(props: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-sm font-medium text-slate-700">
      {props.label}
      <input
        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm font-normal"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </label>
  );
}
